import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { PageHeader, SearchInput } from "@/shared/components/ui-kit";
import { useAppCategories, useApps } from "@/shared/api/apps";
import { AppCard } from "./components/AppCard";

import type { AppRow } from "@/shared/types";

/* The catalog is 1,200 rows, so search and the category facet are the server's
   job and a page is 60 rows. Nothing here filters client-side — a rail count
   and a grid that disagreed would be worse than a round trip. */
const PAGE = 60;
const ALL = "All";
const CONNECTED = "Connected";
const DEBOUNCE_MS = 250;

function RailItem({ name, count, active, onPick }: {
  name: string;
  count: number;
  active: boolean;
  onPick: (name: string) => void;
}) {
  return (
    <button
      onClick={() => onPick(name)}
      className={`press w-full flex items-center gap-2 px-2 py-[6px] rounded-[8px] text-left text-[13px] tracking-[-0.005em] ${
        active
          ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
          : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
      }`}
    >
      <span className="flex-1 truncate">{name}</span>
      <span className="text-[11px] tabular-nums text-[var(--text-quaternary)]">{count}</span>
    </button>
  );
}

export function AppsGrid({ onOpen }: { onOpen: (slug: string) => void }) {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);

  // Every keystroke is a request otherwise, and the catalog is big enough that
  // the sixth one would still be in flight when the answer to the first lands.
  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  /* The page cursor is stored *with* the filter it belongs to rather than reset
     by an effect: an effect would let one render fire `offset=60` against a
     filter that has already changed, which is a wasted request and a page of
     the wrong rows. */
  const filter = `${q}|${category}`;
  const [page, setPage] = useState({ filter, offset: 0 });
  const offset = page.filter === filter ? page.offset : 0;

  const { data, isFetching } = useApps({
    q: q || undefined,
    category: category === ALL || category === CONNECTED ? undefined : category,
    connected: category === CONNECTED ? true : undefined,
    limit: PAGE,
    offset,
  });

  const { data: categories } = useAppCategories();
  // The same predicate the rows carry, counted by the server — `limit: 1`
  // because only `total` is read.
  const { data: connectedPage } = useApps({ connected: true, limit: 1 });

  /* Load-more over a plain paged query: keep what has already arrived and
     append the next page to it. Keyed by the filter, so a page can never be
     appended to a list it was not fetched for. */
  const [loaded, setLoaded] = useState<{ filter: string; rows: AppRow[] }>({ filter, rows: [] });
  useEffect(() => {
    if (!data) return;
    setLoaded((prev) =>
      data.offset === 0 || prev.filter !== filter
        ? { filter, rows: data.items }
        : { filter, rows: [...prev.rows, ...data.items] },
    );
  }, [data, filter]);

  // Connected first: the handful you actually use should never be something you
  // scroll to find. `sort` is stable, so the server's order survives inside
  // each group.
  const rows = useMemo(
    () => (loaded.filter === filter ? [...loaded.rows].sort((a, b) => Number(b.connected) - Number(a.connected)) : []),
    [loaded, filter],
  );

  const total = data?.total ?? 0;
  const remaining = Math.max(total - rows.length, 0);
  const allCount = categories?.reduce((n, c) => n + c.count, 0) ?? 0;

  return (
    <>
      <PageHeader
        title={<Breadcrumb>Apps</Breadcrumb>}
      >
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search apps"
          className="w-[240px]"
        />
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1160px] mx-auto px-4 md:px-6 py-6 flex items-start gap-7 fade-in-up">
          <nav className="w-[172px] shrink-0 sticky top-0 hidden md:block">
            <div className="space-y-[2px]">
              <RailItem name={ALL} count={allCount} active={category === ALL} onPick={setCategory} />
              <RailItem
                name={CONNECTED}
                count={connectedPage?.total ?? 0}
                active={category === CONNECTED}
                onPick={setCategory}
              />
            </div>
            <div className="section-label pb-2 px-2 pt-4">Categories</div>
            <div className="space-y-[2px]">
              {categories?.map((c) => (
                <RailItem
                  key={c.category}
                  name={c.category}
                  count={c.count}
                  active={category === c.category}
                  onPick={setCategory}
                />
              ))}
            </div>
          </nav>

          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {rows.map((app) => (
                <AppCard key={app.slug} app={app} onOpen={onOpen} />
              ))}
            </div>

            {remaining > 0 && (
              <button
                onClick={() => setPage({ filter, offset: offset + PAGE })}
                disabled={isFetching}
                className="press mt-2.5 w-full h-8 flex items-center justify-center gap-1.5 rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] text-[12.5px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)] disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
                Load {Math.min(PAGE, remaining)} more
              </button>
            )}

            {!rows.length && (
              <div className="py-16 text-center text-[13px] text-[var(--text-tertiary)]">
                {isFetching ? "Loading apps…" : q ? `No apps match “${q}”.` : "No apps here yet."}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
